import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface EditMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: any;
  onSuccess: () => void;
}

const ROLE_DESCRIPTIONS = {
  chair: "Leads the PTA, chairs meetings, and oversees all operations",
  treasurer: "Manages finances, budgets, and financial reporting",
  trustee: "Provides governance oversight and strategic guidance",
  secretary: "Records minutes, manages communications, and handles admin",
  catering: "Organizes refreshments and catering for PTA events",
  communications: "Manages newsletters, social media, and public relations",
  community: "Builds relationships with parents and local community",
  assets: "Manages PTA equipment, inventory, and physical resources",
  events: "Plans and coordinates PTA fundraising events",
  occasional_helper: "Volunteers for specific events when available",
};

export function EditMemberDialog({ open, onOpenChange, member, onSuccess }: EditMemberDialogProps) {
  const [mainRoles, setMainRoles] = useState({
    chair: false,
    treasurer: false,
    trustee: false,
    secretary: false,
  });
  const [additionalRoles, setAdditionalRoles] = useState({
    catering: false,
    communications: false,
    community: false,
    assets: false,
    events: false,
    occasional_helper: false,
  });
  const [additionalAccess, setAdditionalAccess] = useState({
    documents: false,
    expenses: false,
  });
  const [documentPermission, setDocumentPermission] = useState<"read" | "edit">("read");
  const [forumAccessDisabled, setForumAccessDisabled] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (member && open) {
      loadMemberData();
    }
  }, [member, open]);

  const loadMemberData = async () => {
    // Load roles
    const { data: roles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', member.id);

    if (roles) {
      const roleNames = roles.map(r => r.role);
      setMainRoles({
        chair: roleNames.includes('chair'),
        treasurer: roleNames.includes('treasurer'),
        trustee: roleNames.includes('trustee'),
        secretary: roleNames.includes('secretary'),
      });
      setAdditionalRoles({
        catering: roleNames.includes('catering'),
        communications: roleNames.includes('communications'),
        community: roleNames.includes('community'),
        assets: roleNames.includes('assets'),
        events: roleNames.includes('events'),
        occasional_helper: roleNames.includes('occasional_helper'),
      });
    }

    // Load additional access
    const { data: access } = await supabase
      .from('member_additional_access')
      .select('*')
      .eq('member_id', member.id);

    if (access) {
      setAdditionalAccess({
        documents: access.some(a => a.access_type === 'documents'),
        expenses: access.some(a => a.access_type === 'expenses'),
      });
      const docAccess = access.find(a => a.access_type === 'documents');
      if (docAccess?.permission_level) {
        setDocumentPermission(docAccess.permission_level as "read" | "edit");
      }
    }

    // Load forum access status
    setForumAccessDisabled(member.forum_access_disabled || false);
  };

  const handleSubmit = async () => {
    setLoading(true);

    // Validate roles
    if (mainRoles.chair && mainRoles.treasurer) {
      toast({
        title: "Invalid Selection",
        description: "A member cannot be both Chair and Treasurer",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    // Delete existing roles
    await supabase.from('user_roles').delete().eq('user_id', member.id);

    // Add new roles
    const rolesToAdd = [
      ...Object.entries(mainRoles).filter(([_, v]) => v).map(([k]) => k),
      ...Object.entries(additionalRoles).filter(([_, v]) => v).map(([k]) => k),
    ];

    if (rolesToAdd.length > 0) {
      await supabase.from('user_roles').insert(
        rolesToAdd.map(role => ({
          user_id: member.id,
          role: role as any,
        }))
      );
    }

    // Update member role field and forum access
    await supabase
      .from('members')
      .update({
        role: rolesToAdd.join(', '),
        forum_access_disabled: forumAccessDisabled
      })
      .eq('id', member.id);

    // Delete existing access
    await supabase.from('member_additional_access').delete().eq('member_id', member.id);

    // Add new access
    const accessToAdd = Object.entries(additionalAccess)
      .filter(([_, v]) => v)
      .map(([type]) => ({
        member_id: member.id,
        access_type: type,
        permission_level: type === 'documents' ? documentPermission : null,
      }));

    if (accessToAdd.length > 0) {
      await supabase.from('member_additional_access').insert(accessToAdd);
    }

    setLoading(false);
    toast({
      title: "Success",
      description: "Member updated successfully"
    });
    onSuccess();
    onOpenChange(false);
  };

  const hasOccasionalHelper = additionalRoles.occasional_helper;
  const hasMainRole = Object.values(mainRoles).some((v) => v);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Member</DialogTitle>
          <DialogDescription>Update roles and permissions for {member?.full_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {/* Main Roles */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">Main Roles</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Chair and Treasurer cannot be on same member
              </p>
            </div>
            <TooltipProvider>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(mainRoles).map(([role, value]) => (
                  <div
                    key={role}
                    className="flex items-center justify-between p-3 border border-border rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`edit-${role}`} className="cursor-pointer capitalize">
                        {role}
                      </Label>
                       <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs z-50 bg-popover">
                          <p className="text-sm">{ROLE_DESCRIPTIONS[role as keyof typeof ROLE_DESCRIPTIONS]}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Switch
                      id={`edit-${role}`}
                      checked={value}
                      onCheckedChange={(checked) => {
                        // If enabling chair, disable treasurer (and vice versa)
                        if (checked) {
                          if (role === 'chair') {
                            setMainRoles((prev) => ({ ...prev, chair: true, treasurer: false }));
                          } else if (role === 'treasurer') {
                            setMainRoles((prev) => ({ ...prev, treasurer: true, chair: false }));
                          } else {
                            setMainRoles((prev) => ({ ...prev, [role]: checked }));
                          }
                        } else {
                          setMainRoles((prev) => ({ ...prev, [role]: checked }));
                        }
                      }}
                      disabled={hasOccasionalHelper}
                    />
                  </div>
                ))}
              </div>
            </TooltipProvider>
          </div>

          <Separator />

          {/* Additional Roles */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">Additional Roles</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Occasional helpers cannot have main roles
              </p>
            </div>
            <TooltipProvider>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(additionalRoles).map(([role, value]) => (
                  <div
                    key={role}
                    className="flex items-center justify-between p-3 border border-border rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`edit-${role}`} className="cursor-pointer capitalize">
                        {role.replace("_", " ")}
                      </Label>
                       <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs z-50 bg-popover">
                          <p className="text-sm">{ROLE_DESCRIPTIONS[role as keyof typeof ROLE_DESCRIPTIONS]}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <Switch
                      id={`edit-${role}`}
                      checked={value}
                      onCheckedChange={(checked) =>
                        setAdditionalRoles((prev) => ({ ...prev, [role]: checked }))
                      }
                      disabled={role === "occasional_helper" ? hasMainRole : hasOccasionalHelper}
                    />
                  </div>
                ))}
              </div>
            </TooltipProvider>
          </div>

          <Separator />

          {/* Additional Access */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">Additional Access</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="edit-documents" className="cursor-pointer">
                    Documents
                  </Label>
                  {additionalAccess.documents && (
                    <div className="mt-2 ml-4">
                      <Select
                        value={documentPermission}
                        onValueChange={(v: "read" | "edit") => setDocumentPermission(v)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="read">Read Only</SelectItem>
                          <SelectItem value="edit">Edit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <Switch
                  id="edit-documents"
                  checked={additionalAccess.documents}
                  onCheckedChange={(checked) =>
                    setAdditionalAccess((prev) => ({ ...prev, documents: checked }))
                  }
                  disabled={hasOccasionalHelper}
                />
              </div>
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="edit-expenses" className="cursor-pointer">
                    Expense Claims
                  </Label>
                </div>
                <Switch
                  id="edit-expenses"
                  checked={additionalAccess.expenses}
                  onCheckedChange={(checked) =>
                    setAdditionalAccess((prev) => ({ ...prev, expenses: checked }))
                  }
                  disabled={hasOccasionalHelper}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Forum Access Control */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Forum Access</h3>
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="forum-access">Disable Forum Access</Label>
                <p className="text-xs text-muted-foreground">Ban this user from accessing the forum</p>
              </div>
              <Switch
                id="forum-access"
                checked={forumAccessDisabled}
                onCheckedChange={setForumAccessDisabled}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
